import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of } from "rxjs";
import { map, mergeMap, catchError } from "rxjs/operators";
import { PrintKioskActions } from "@core/state/print-kiosks/print-kiosks.actions";
import { DeviceDataService, deviceToPrintKiosk } from "@core/services/datasources/device-data.service";
import { createNormalizedError } from "@core/utilities/error-handler.util";

@Injectable({ providedIn: 'root' })
export class PrintKiosksEffects {
  private readonly actions$ = inject(Actions);
  private readonly deviceDataService = inject(DeviceDataService);

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrintKioskActions.load),
      mergeMap(() =>
        this.deviceDataService.getAllPrintKiosks().pipe(
          map((printKiosks) => PrintKioskActions.loadSuccess({ printKiosks })),
          catchError((error) => {
            const normalizedError = createNormalizedError(error);
            return of(PrintKioskActions.loadFailure({ error: normalizedError }));
          })
        )
      )
    )
  );

  add$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrintKioskActions.add),
      map((action) => action.command),
      mergeMap((command) =>
        this.deviceDataService.addPrintKiosk(command).pipe(
          map((device) => deviceToPrintKiosk(device)),
          map((printKiosk) => PrintKioskActions.addSuccess({ printKiosk })),
          catchError((error) => {
            const normalizedError = createNormalizedError(error);
            return of(PrintKioskActions.addFailure({ error: normalizedError }));
          })
        )
      )
    )
  );

  remove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrintKioskActions.remove),
      map((action) => action.command),
      mergeMap((command) =>
        this.deviceDataService.deleteDevice(command.id).pipe(
          map(() => PrintKioskActions.removeSuccess({ id: command.id })),
          catchError((error) => {
            const normalizedError = createNormalizedError(error);
            return of(PrintKioskActions.removeFailure({ error: normalizedError }));
          })
        )
      )
    )
  );

  linkToPrinter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrintKioskActions.linkToPrinter),
      map((action) => action.command),
      mergeMap((command) =>
        this.deviceDataService.createDeviceRelationship(command).pipe(
          mergeMap(() => [
            PrintKioskActions.linkToPrinterSuccess(),
            PrintKioskActions.load(),
          ]),
          catchError((error) => {
            const normalizedError = createNormalizedError(error);
            return of(PrintKioskActions.linkToPrinterFailure({ error: normalizedError }));
          })
        )
      )
    )
  );
}
